(() => {
  const MAX_WAIT_MS = 8000;
  const POLL_MS = 200;

  function getSettingsFromWindow() {
    const settings = window.jdgmSettings || {};
    const shopDomain =
      settings.shop_domain ||
      settings.shopDomain ||
      window.Shopify?.shop ||
      null;
    const apiToken =
      settings.public_api_token ||
      settings.api_token ||
      settings.token ||
      settings.publicToken ||
      settings.public_token ||
      null;

    return { shopDomain, apiToken };
  }

  function waitForSettings() {
    return new Promise((resolve) => {
      const started = Date.now();

      const check = () => {
        const { shopDomain, apiToken } = getSettingsFromWindow();
        if (shopDomain && apiToken) {
          resolve({ shopDomain, apiToken });
          return;
        }

        if (Date.now() - started >= MAX_WAIT_MS) {
          resolve({
            shopDomain: shopDomain || window.Shopify?.shop || null,
            apiToken: apiToken || null,
          });
          return;
        }

        window.setTimeout(check, POLL_MS);
      };

      check();
    });
  }

  function formatReviewerName(name) {
    if (!name) return '';
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '';
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function renderStars(rating) {
    const rounded = Math.round(Number(rating) || 0);
    let html = '';

    for (let i = 1; i <= 5; i += 1) {
      html += `<span class="customer-reviews-carousel__star${i <= rounded ? '' : ' is-empty'}" aria-hidden="true">★</span>`;
    }

    return html;
  }

  function buildCard(review) {
    const title = review.title
      ? `<h3 class="customer-reviews-carousel__title heading text-base-xl leading-tight">${escapeHtml(review.title)}</h3>`
      : '';
    const body = review.body
      ? `<div class="customer-reviews-carousel__text rte text-sm leading-normal"><p>${escapeHtml(review.body)}</p></div>`
      : '';
    const author = review.reviewerName
      ? `<p class="customer-reviews-carousel__author text-sm font-medium">${escapeHtml(review.reviewerName)}</p>`
      : '';

    return `
      <article class="customer-reviews-carousel__card card flex flex-col w-full">
        <div class="customer-reviews-carousel__stars" aria-label="${review.rating} out of 5 stars">
          ${renderStars(review.rating)}
        </div>
        ${title}
        ${body}
        ${author}
      </article>
    `;
  }

  function readEmbeddedData(element) {
    const id = element.dataset.reviewsJson;
    if (!id) return null;
    const node = document.getElementById(id);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  function normalizeReview(review) {
    const rating = Number(review.rating || review.score || review.stars || 0);
    const reviewerName = formatReviewerName(
      review.reviewer?.name ||
        review.reviewer_name ||
        review.reviewerName ||
        review.name ||
        review.author ||
        review.reviewer_name_display
    );

    return {
      rating,
      title: review.title || review.review_title || '',
      body: review.body || review.review || review.content || review.comment || '',
      reviewerName,
    };
  }

  function reviewsFromWidgetData(widgetData) {
    if (!widgetData) return [];

    const candidates = [
      widgetData.reviews,
      widgetData.revs,
      widgetData.product_reviews,
      widgetData.review_list,
      widgetData?.data?.reviews,
      widgetData?.widget?.reviews,
    ];

    for (const list of candidates) {
      if (Array.isArray(list) && list.length) return list;
    }

    return [];
  }

  function reviewsFromWidgetHtml(html) {
    if (!html) return [];

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nodes = [
      ...doc.querySelectorAll('.jdgm-rev, .jdgm-rev-card, [data-verified-buyer], .jdgm-carousel-item'),
    ];

    return nodes
      .map((node) => {
        const rating =
          Number(
            node.getAttribute('data-rating') ||
              node.querySelector('[data-score]')?.getAttribute('data-score') ||
              node.querySelector('.jdgm-rev__rating')?.getAttribute('data-score') ||
              (node.querySelectorAll('.jdgm-star.jdgm--on, .jdgm-star.jdgm-star--on').length)
          ) || 0;

        const title =
          node.querySelector('.jdgm-rev__title, .jdgm-rev-title, .jdgm-carousel-item__review-title')?.textContent?.trim() ||
          '';
        const body =
          node.querySelector('.jdgm-rev__body, .jdgm-rev-content, .jdgm-carousel-item__review-body, .jdgm-rev__content')?.textContent?.trim() ||
          '';
        const name =
          node.querySelector('.jdgm-rev__author, .jdgm-rev-author, .jdgm-carousel-item__reviewer-name, [class*="author"]')?.textContent?.trim() ||
          '';

        return { rating, title, body, reviewer_name: name };
      })
      .filter((review) => review.rating || review.body || review.title);
  }

  async function fetchJson(url) {
    const response = await fetch(url);
    if (!response.ok) return null;
    return response.json();
  }

  async function fetchJudgeMeProductId(shopDomain, apiToken, productId) {
    const params = new URLSearchParams({
      shop_domain: shopDomain,
      api_token: apiToken,
      external_id: String(productId),
    });

    const data = await fetchJson(`https://judge.me/api/v1/products/-1?${params}`);
    return data?.product?.id ?? null;
  }

  function reviewBelongsToProduct(review, productId, judgeMeProductId) {
    const externalIds = [
      review.product_external_id,
      review.external_id,
      review.product?.external_id,
      review.product?.shopify_product_id,
    ]
      .filter(Boolean)
      .map(String);

    if (externalIds.includes(String(productId))) return true;
    if (judgeMeProductId != null && String(review.product_id) === String(judgeMeProductId)) return true;
    return false;
  }

  async function fetchProductReviews(shopDomain, apiToken, productId, minNeeded) {
    const judgeMeProductId = await fetchJudgeMeProductId(shopDomain, apiToken, productId);
    const collected = [];
    const maxPages = 5;

    for (let page = 1; page <= maxPages; page += 1) {
      const params = new URLSearchParams({
        shop_domain: shopDomain,
        api_token: apiToken,
        page: String(page),
        per_page: '50',
      });

      if (judgeMeProductId) {
        params.set('product_id', String(judgeMeProductId));
      } else {
        params.set('product_external_id', String(productId));
      }

      const data = await fetchJson(`https://judge.me/api/v1/reviews?${params}`);
      const batch = Array.isArray(data?.reviews) ? data.reviews : [];
      if (!batch.length) break;

      const productReviews = batch.filter((review) =>
        reviewBelongsToProduct(review, productId, judgeMeProductId)
      );

      // If the API ignored the product filter and returned unrelated reviews, stop.
      if (!productReviews.length) break;

      collected.push(...productReviews);

      if (collected.length >= minNeeded || batch.length < 50) break;
    }

    return collected;
  }

  async function fetchWidgetPage(shopDomain, productId, page, perPage) {
    const params = new URLSearchParams({
      url: `https://${shopDomain}`,
      shop_domain: shopDomain,
      platform: 'shopify',
      product_id: String(productId),
      page: String(page),
      per_page: String(perPage),
    });

    const data = await fetchJson(`https://api.judge.me/reviews/reviews_for_widget?${params}`);
    const batch = reviewsFromWidgetHtml(data?.html);
    return {
      batch,
      totalCount: Number(data?.total_count),
    };
  }

  // Public Judge.me widget endpoint — no API token required.
  // Fetch pages in parallel batches so we don't wait page-by-page.
  async function fetchWidgetReviews(shopDomain, productId, minNeeded) {
    const collected = [];
    const maxPages = 8;
    const perPage = 20;
    const parallel = 3;

    for (let start = 1; start <= maxPages; start += parallel) {
      const pages = [];
      for (let page = start; page < start + parallel && page <= maxPages; page += 1) {
        pages.push(page);
      }

      const results = await Promise.all(
        pages.map((page) => fetchWidgetPage(shopDomain, productId, page, perPage))
      );

      let reachedEnd = false;
      for (const result of results) {
        const batch = result.batch || [];
        if (!batch.length) {
          reachedEnd = true;
          break;
        }
        collected.push(...batch);
        if (
          batch.length < perPage ||
          (Number.isFinite(result.totalCount) && collected.length >= result.totalCount)
        ) {
          reachedEnd = true;
        }
      }

      if (collected.length >= minNeeded || reachedEnd) break;
    }

    return collected;
  }

  function cacheKey(productId, minRating, limit) {
    return `peachade-reviews:${productId}:${minRating}:${limit}`;
  }

  function readCache(productId, minRating, limit) {
    try {
      const raw = sessionStorage.getItem(cacheKey(productId, minRating, limit));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.reviews)) return null;
      if (Date.now() - Number(parsed.savedAt || 0) > 60 * 60 * 1000) return null;
      return parsed.reviews;
    } catch (error) {
      return null;
    }
  }

  function writeCache(productId, minRating, limit, reviews) {
    try {
      sessionStorage.setItem(
        cacheKey(productId, minRating, limit),
        JSON.stringify({ savedAt: Date.now(), reviews })
      );
    } catch (error) {
      // Ignore quota / private mode failures.
    }
  }

  function reviewKey(review) {
    const normalized = normalizeReview(review);
    return `${normalized.rating}|${normalized.title}|${normalized.body}|${normalized.reviewerName}`;
  }

  function mergeReviews(...lists) {
    const seen = new Set();
    const merged = [];

    for (const list of lists) {
      for (const review of list || []) {
        const key = reviewKey(review);
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(review);
      }
    }

    return merged;
  }

  function updateSummary(element, ratingValue, ratingCount) {
    if (ratingValue == null || ratingCount == null || Number(ratingCount) <= 0) return;

    const summary = element.querySelector('.product-reviews-highlight__summary');
    if (!summary) return;

    let ratingBlock = summary.querySelector('.product-reviews-highlight__rating-value')?.parentElement;
    if (!ratingBlock) {
      ratingBlock = document.createElement('div');
      ratingBlock.className = 'flex flex-col items-center gap-2';
      ratingBlock.innerHTML = `
        <div class="customer-reviews-carousel__stars" aria-label="${ratingValue} out of 5 stars">${renderStars(ratingValue)}</div>
        <p class="product-reviews-highlight__rating-value heading">${escapeHtml(String(ratingValue))}</p>
        <p class="product-reviews-highlight__count text-sm" data-review-count></p>
      `;
      summary.appendChild(ratingBlock);
    }

    const countEl = summary.querySelector('[data-review-count]');
    if (countEl) {
      countEl.hidden = false;
      countEl.textContent = `(${ratingCount} review${Number(ratingCount) === 1 ? '' : 's'})`;
    }
  }

  function filterReviews(reviews, minRating, limit) {
    return reviews
      .map(normalizeReview)
      .filter((review) => review.rating >= minRating && (review.body || review.title))
      .slice(0, limit);
  }

  function paintReviews(element, filtered, embedded) {
    const grid = element.querySelector('[data-reviews-grid]');
    const controls = element.querySelector('[data-reviews-controls]');
    if (!grid || !filtered.length) return;

    updateSummary(
      element,
      embedded.ratingValue,
      embedded.ratingCount || filtered.length
    );

    grid.innerHTML = filtered.map(buildCard).join('');
    element.removeAttribute('hidden');

    if (controls) {
      controls.toggleAttribute('hidden', filtered.length <= 1);
    }
  }

  async function init(element) {
    const productId = element.dataset.productId;
    const limit = Number(element.dataset.limit || 6);
    const minRating = Number(element.dataset.minRating || 4);
    const grid = element.querySelector('[data-reviews-grid]');
    const embedded = readEmbeddedData(element) || {};

    if (!productId || !grid) return;

    const cached = readCache(productId, minRating, limit);
    if (cached?.length) {
      paintReviews(element, filterReviews(cached, minRating, limit), embedded);
      // Still refresh in the background below when embedded is short.
    }

    let reviews = mergeReviews(
      cached,
      reviewsFromWidgetData(embedded.widgetData),
      reviewsFromWidgetHtml(embedded.widgetHtml)
    );

    let filtered = filterReviews(reviews, minRating, limit);

    // Show whatever we already have immediately (embedded / cache).
    if (filtered.length) {
      paintReviews(element, filtered, embedded);
    }

    // Embedded Judge.me HTML only includes a small first page. Fetch more when needed.
    if (filtered.length < limit) {
      let shopDomain = embedded.shopDomain || window.Shopify?.shop || null;
      let apiToken = embedded.apiToken || getSettingsFromWindow().apiToken || null;

      // Shop domain is enough for the public widget API. Only wait on Judge.me
      // settings when we don't already know the shop.
      if (!shopDomain) {
        const liveSettings = await waitForSettings();
        shopDomain = liveSettings.shopDomain;
        apiToken = apiToken || liveSettings.apiToken;
      }

      const minNeeded = Math.max(limit * 3, 20);

      try {
        const fetches = [];
        // Widget endpoint is public and usually enough — start it immediately.
        if (shopDomain) {
          fetches.push(fetchWidgetReviews(shopDomain, productId, minNeeded));
        }
        // Official API in parallel when token is present (larger pages).
        if (apiToken && shopDomain) {
          fetches.push(fetchProductReviews(shopDomain, apiToken, productId, minNeeded));
        }

        if (fetches.length) {
          const results = await Promise.all(fetches);
          reviews = mergeReviews(reviews, ...results);
        }
      } catch (error) {
        console.error(error);
      }

      filtered = filterReviews(reviews, minRating, limit);
      if (filtered.length) {
        writeCache(productId, minRating, limit, filtered);
      }
    }

    if (!filtered.length) {
      element.toggleAttribute('hidden', true);
      return;
    }

    paintReviews(element, filtered, embedded);
  }

  function boot() {
    document.querySelectorAll('product-reviews-highlight').forEach((element) => {
      if (element.dataset.initialized === 'true') return;
      element.dataset.initialized = 'true';
      init(element);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
